import styled from "styled-components";
import { FC, MouseEvent, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ActionsWrap, Td } from "@/components/Table";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import Text from "@/components/Text";
import SceneNotesModal from "./SceneNotesModal";
import { ProductionSceneNoteResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";

const SceneNumber = styled(Text)`
  background: ${({ theme }) => theme.colors.lightGray};
  width: 16px;
  height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 6px;
`;

const TitleHolder = styled.div`
  display: flex;
  align-items: flex-start;
`;

interface Props {
  note: ProductionSceneNoteResource;
  sceneIndex: number;
}

const SceneNotesTableRow: FC<Props> = ({ note, sceneIndex }) => {
  const [showModal, setShowModal] = useState(false);

  const { projectId } = useParams();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        "Are you sure you want to delete this note?"
      );

      if (confirm) {
        await api.delete<never, { id: number }>(
          `/v1/production/scenes/notes/${note.id}`
        );
        alerts.success("Note successfully deleted");

        queryClient.invalidateQueries(["production_projects", { projectId }]);
      }
    } catch (err) {
      alerts.error();
    }
  };

  return (
    <tr>
      <Td>
        <TitleHolder>
          <SceneNumber $type="extraSmall">{sceneIndex}</SceneNumber>
          {note.title}
        </TitleHolder>
      </Td>
      <Td>{note.content}</Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              as="button"
              onClick={() => setShowModal(true)}
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
      <AnimatePresence>
        {!!showModal && (
          <SceneNotesModal note={note} onClose={() => setShowModal(false)} />
        )}
      </AnimatePresence>
    </tr>
  );
};

export default SceneNotesTableRow;
